import { faArrowLeft, faBars } from '@fortawesome/free-solid-svg-icons'
import React, { Suspense, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import SidebarItem from './component/SidebarItem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fileSrc } from '../../component/FileLoader'
import { useQuery } from '@tanstack/react-query'
import { checkPermission, get_request_api, useSettings } from '@velocity/shared'
import Notification from './component/Notification'
import UserProfile from './component/UserProfile'
import { ADMIN, defaultLogo } from '../../consts'
import { GuidedSetup } from './component/GuidedSetup'
import { useTranslation } from 'react-i18next'
import { guided_setup } from './component/guides'
import { PermissionAction, useAuth } from '@velocity/shared'
import {
  MdOutlineAccountCircle,
  MdOutlineAddBusiness,
  MdOutlineAnalytics,
  MdOutlineBarChart,
  MdOutlineBusinessCenter,
  MdOutlineCalendarMonth,
  MdOutlineEventBusy,
  MdOutlineFolder,
  MdOutlineGroup,
  MdOutlineLibraryBooks,
  MdOutlinePayments,
  MdOutlineSettings
} from 'react-icons/md'

export default function WebLayout() {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { pathname } = useLocation()
  const [isMenuOpen, setMenuOpen] = useState(false)
  const [openGuide, setOpenGuide] = useState(pathname === '/dashboard')
  const [completed_guided_setup, setCompletedGuidedSetup] = useState(['all'])
  const { records: settings } = useSettings()
  const { data: alerts } = useQuery({
    queryKey: ['alerts'],
    queryFn: get_request_api,
    enabled: checkPermission(currentUser, 'settings', PermissionAction.Read)
  })
  const [showMobileSideBar, setShowMobileSideBar] = useState(false)

  let styleHearder: any = {
    fontWeight: '500',
    lineHeight: '24px',
    letterSpacing: '0.1px',
    fontFamily: 'Inter'
  }
  return (
    <div className='h-screen bg-[#F1F4FB]'>
      <header
        className={`no-print sticky top-0 inset-x-0 flex flex-wrap sm:justify-start sm:flex-nowrap z-30
         w-full bg-white border-b text-sm py-2.5 sm:py-4  transition-all duration-300 
         ${isMenuOpen && 'lg:ps-24 '}
         ${!isMenuOpen && 'lg:ps-64 '}`}
      >
        <nav
          className='flex basis-full items-center w-full mx-auto px-4 sm:px-6 md:px-8'
          aria-label={t('Global')}
        >
          <div className='me-5 lg:me-0 lg:hidden'>
            <div className='flex-none text-xl font-semibold flex'>
              <button
                onClick={() => setShowMobileSideBar(!showMobileSideBar)}
                type='button'
                className='pr-2 text-gray-500 hover:text-gray-600'
              >
                <span className='sr-only'>{t('Toggle Navigation')}</span>
                <svg
                  className='flex-shrink-0 size-6'
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='3' x2='21' y1='6' y2='6' />
                  <line x1='3' x2='21' y1='12' y2='12' />
                  <line x1='3' x2='21' y1='18' y2='18' />
                </svg>
              </button>
              {settings?.business_logo ?
                <img
                  className='sm:h-8 h-14 object-contain'
                  src={fileSrc({
                    id: settings?.business_logo,
                    provider: 'cloudinary'
                  })}
                  alt=''
                />
              : null}
            </div>
          </div>

          <div className='w-full flex items-center  justify-end ms-auto sm:justify-between sm:gap-x-3 sm:order-3'>
            <div className='hidden lg:flex item-center'>
              <button className='mr-3' onClick={() => setMenuOpen(!isMenuOpen)}>
                {!isMenuOpen && <FontAwesomeIcon icon={faBars} />}
                {isMenuOpen && <FontAwesomeIcon icon={faArrowLeft} />}
              </button>
              <p
                style={styleHearder}
                className={`px-2 py-auto text-sm uppercase truncate ${settings?.business_logo ? 'invisible' : ''} lg:visible`}
              >
                {settings?.business_name}
              </p>
            </div>
            <div className='flex flex-row items-center justify-end gap-2'>
              {!completed_guided_setup?.includes('all') ?
                <span
                  className='hidden md:flex cursor-pointer text-gray-500 hover:text-gray-600'
                  onClick={() => setOpenGuide(!openGuide)}
                >
                  {t('Setup Guide')}&nbsp;
                  <span className='inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset'>
                    {completed_guided_setup.length}/{guided_setup.length}
                  </span>
                </span>
              : null}
              <Notification />
              <UserProfile />
            </div>
          </div>
        </nav>
      </header>
      {showMobileSideBar && (
        <button
          onClick={() => setShowMobileSideBar(!showMobileSideBar)}
          className='text-gray-400 hover:text-white absolute bg-black z-30 opacity-5 h-screen w-full top-0 m-1 right-0'
        >
          {/* &times; */}
        </button>
      )}
      <div
        id='application-sidebar'
        className={`no-print transition-all duration-300 transform fixed lg:top-0 lg:start-0 lg:bottom-0 ${!isMenuOpen ? 'lg:w-64' : 'lg:w-28'} 
           bg-white border-e border-gray-200 fixed z-30 pt-3 pb-10 flex flex-col h-screen
           block lg:end-auto lg:bottom-0 lg:translate-x-0 ${showMobileSideBar ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className='px-6'>
          <span
            className='flex-none text-xl flex font-semibold '
            aria-label={t('Brand')}
          >
            <img
              className='h-14 object-contain m-auto hidden lg:block'
              src={
                settings?.business_logo ?
                  fileSrc({
                    id: settings?.business_logo,
                    provider: 'cloudinary'
                  })
                : defaultLogo
              }
              alt=''
            />
          </span>
        </div>
        <nav
          className='hs-accordion-group pt-2 pl-5 w-full flex flex-col h-full'
          data-hs-accordion-always-open
        >
          <ul className='space-y-1.5 overflow-y-auto pr-4 flex-1'>
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='dashboard'
              id='dashboard'
              title='Dashboard'
              Icon={MdOutlineAnalytics}
              onClick={() => setShowMobileSideBar(false)}
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='schedules'
              id='schedules'
              title='Schedules'
              Icon={MdOutlineCalendarMonth}
              dropdownList={[
                {
                  link: '',
                  title: 'Schedule Calendar',
                  id: 'schedules'
                },
                {
                  link: 'overtime-requests',
                  title: 'Overtime Requests',
                  id: 'overtime-requests'
                },
                {
                  link: 'schedule-reports',
                  title: 'Schedule Reports',
                  id: 'schedule-reports'
                },
                {
                  link: 'availabilities',
                  title: 'Availabilities',
                  id: 'availabilities'
                }
              ]}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Choose date, assign staff, define timing. Drag-and-drop enabled.'
            />

            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='users'
              id='users'
              title='Team'
              Icon={MdOutlineGroup}
              dropdownList={[
                {
                  link: '',
                  title: 'Team Members',
                  id: 'users'
                },
                {
                  link: 'roles',
                  title: 'Roles',
                  id: 'roles'
                }
              ]}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Manage your team members and their roles to control access across the platform.'
            />

            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='finances'
              id='finances'
              title='Finance'
              Icon={MdOutlinePayments}
              dropdownList={[
                { link: 'invoices', title: 'Invoices', id: 'invoices' },
                { link: 'payments', title: 'Payments', id: 'payments' },
                { link: 'payrolls', title: 'Payrolls', id: 'payrolls' },
                { link: 'expenses', title: 'Expenses', id: 'expenses' },
                { link: 'estimates', title: 'Estimates', id: 'estimates' }
              ]}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Manage invoices, payrolls, and expenses. This lets you track your business finances.'
            />

            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='timeoff'
              id='timeoff'
              title='Time off'
              Icon={MdOutlineEventBusy}
              dropdownList={[
                {
                  link: 'timeoff-requests',
                  title: 'Timeoff Requests',
                  id: 'timeoff-requests'
                },
                {
                  link: 'timeoff-categories',
                  title: 'Timeoff Categories',
                  id: 'timeoff-categories'
                }
              ]}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Track vacation, sick days, and other time off. This lets you manage your team’s availability.'
            />
            {/* <SidebarItem
              isMenuOpen={isMenuOpen}
              link='report'
              id='report'
              title='Report'
              Icon={MdOutlineBarChart}
              dropdownList={[
                {
                  link: 'timesheets',
                  title: 'Timesheet',
                  id: 'timesheets'
                },
                {
                  link: 'payroll',
                  title: 'Payroll',
                  id: 'payroll'
                },
                {
                  link: 'invoice',
                  title: 'Invoice',
                  id: 'invoice'
                }
              ]}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Generate reports for timesheets, payrolls, and invoices. This lets you analyze your business performance.'
            /> */}
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='services'
              id='services'
              title='Services'
              Icon={MdOutlineBusinessCenter}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Define what your team delivers so you can assign staff, track time, and link it to client jobs.'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='locations'
              id='locations'
              title='Clients'
              Icon={MdOutlineAddBusiness}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Manage your business clients. This lets you assign staff, track hours, and send invoices.'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='assets'
              id='assets'
              title='Assets'
              Icon={MdOutlineFolder}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Upload Business files and folders'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='reports'
              id='reports'
              title='Reports'
              Icon={MdOutlineBarChart}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Generate reports for timesheets, payrolls, and invoices. This lets you analyze your business performance.'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='settings'
              id='settings'
              title='Settings'
              Icon={MdOutlineSettings}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Configure your account settings and preferences.'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='audit-logs'
              id='audit-logs'
              title='Audit'
              Icon={MdOutlineLibraryBooks}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Audit logs for tracking changes and actions within the system.'
            />
            <SidebarItem
              isMenuOpen={isMenuOpen}
              link='profile'
              id='profile'
              title='Profile'
              Icon={MdOutlineAccountCircle}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Manage your profile settings and preferences.'
            />
            {/* <SidebarItem
              isMenuOpen={isMenuOpen}
              link='support'
              id='support'
              title='Support'
              Icon={MdOutlineSupport}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Get help and support for using the platform.'
            /> */}
            {/* <SidebarItem
              isMenuOpen={isMenuOpen}
              link='faq'
              id='faq'
              title='FAQ'
              icon={faQuestion}
              onClick={() => setShowMobileSideBar(false)}
              tooltip='Frequently asked questions about the platform.'
            /> */}
          </ul>
        </nav>
      </div>
      <div
        id='c-close'
        className={`w-full h-max pt-4 sm:pt-7 px-4 transition-all duration-300 md:px-8 
        ${!isMenuOpen ? 'lg:ps-72' : 'lg:ps-32'} 
        bg-[#F1F4FB]`}
      >
        <Suspense>
          {
            alerts?.length ?
              <></>
              // <Alerts alerts={alerts} />
            : null
          }
          {ADMIN?.includes(currentUser.role?.name!) ?
            <GuidedSetup
              settings={settings}
              completed_guided_setup={completed_guided_setup}
              setCompletedGuidedSetup={setCompletedGuidedSetup}
              openGuide={openGuide}
            />
          : null}
          <Outlet />
        </Suspense>
        <br />
        <br /> <br />
      </div>
    </div>
  )
}
